"use client";

import {useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {NextStep, NextStepProvider, useNextStep} from "nextstepjs";
import GailGptWelcomeTourCard from "@/components/chat/GailGptWelcomeTourCard";
import {
   buildGailGptWelcomeTourSteps,
   GAILGPT_WELCOME_TOUR_NAME,
   type GailGptWelcomeTourVisibility,
} from "@/components/chat/GailGptWelcomeTourSteps";
import type {UserAcknowledgementStatus} from "@/utils/acknowledgements/userAcknowledgements";

interface GailGptWelcomeTourProps {
   children: React.ReactNode;
   shouldStart: boolean;
   onFinish?: (status: UserAcknowledgementStatus) => void;
   visibility: GailGptWelcomeTourVisibility;
}

function GailGptWelcomeTourStarter({shouldStart}: Readonly<{shouldStart: boolean}>) {
   const {startNextStep} = useNextStep();
   const startedRef = useRef(false);

   useEffect(() => {
      if (!shouldStart || startedRef.current) return;

      const timer = setTimeout(() => {
         startedRef.current = true;
         startNextStep(GAILGPT_WELCOME_TOUR_NAME);
      }, 500);

      return () => clearTimeout(timer);
   }, [shouldStart, startNextStep]);

   useEffect(() => {
      if (!shouldStart) {
         startedRef.current = false;
      }
   }, [shouldStart]);

   return null;
}

export default function GailGptWelcomeTour({
   children,
   shouldStart,
   onFinish,
   visibility,
}: Readonly<GailGptWelcomeTourProps>) {
   const {t} = useTranslation("assistants");
   const steps = useMemo(
      () => buildGailGptWelcomeTourSteps(t, visibility),
      [t, visibility.showAssistantSelector, visibility.showGailsNav, visibility.showHistoryNav]
   );

   return (
      <NextStepProvider>
         <NextStep
            steps={steps}
            onComplete={() => onFinish?.("completed")}
            onSkip={() => onFinish?.("skipped")}
            cardComponent={GailGptWelcomeTourCard}
         >
            <GailGptWelcomeTourStarter shouldStart={shouldStart} />
            {children}
         </NextStep>
      </NextStepProvider>
   );
}
